import React, { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import quoteService from '../../services/quoteService';
import CalendarButton from '../../components/UI/CalendarButton/CalendarButton';
import VerifiedBadge from '../../components/UI/VerifiedBadge/VerifiedBadge';
import DrawableMannequin from '../../components/Quotes/DrawableMannequin';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
// import paymentService from '../../services/paymentService';
import './QuotesPage.css';

const QuotesPage = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const initialNavRef = React.useRef(false);
    const [quotes, setQuotes] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [selectedQuote, setSelectedQuote] = React.useState(null);
    const [showMobileDetail, setShowMobileDetail] = React.useState(false);
    const [showBodyMap, setShowBodyMap] = React.useState(true);
    const [statusFilter, setStatusFilter] = React.useState('active'); // 'active', 'approved', 'rejected'

    // Proposal Form State
    const [proposalPriceMin, setProposalPriceMin] = React.useState('');
    const [proposalPriceMax, setProposalPriceMax] = React.useState('');
    const [proposalDate, setProposalDate] = React.useState('');
    const [refinedBookingDate, setRefinedBookingDate] = React.useState('');
    const [showProposalForm, setShowProposalForm] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState('chat'); // 'chat' or 'details'

    // Chat states
    const [messages, setMessages] = React.useState([]);
    const [newMessage, setNewMessage] = React.useState('');
    const [loadingMessages, setLoadingMessages] = React.useState(false);
    const messagesEndRef = React.useRef(null);
    const touchStartX = React.useRef(0);
    const touchEndX = React.useRef(0);

    // User info
    const currentUser = JSON.parse(localStorage.getItem('user')) || {};
    const currentUserId = currentUser.id || currentUser.user_id;

    // Track previous quote statuses for change detection (using ref to avoid re-render loop)
    const previousStatuses = React.useRef({});



    // Load messages when a quote is selected
    React.useEffect(() => {
        if (selectedQuote) {
            fetchMessages(selectedQuote.id);
        } else {
            setMessages([]);
        }
    }, [selectedQuote]);

    React.useEffect(() => {
        // Only auto-scroll on initial load, not on polling updates
        if (loadingMessages) {
            scrollToBottom();
        }
    }, [messages, loadingMessages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchQuotes = useCallback(async (silent = false) => {
        try {
            if (!silent) setLoading(true);

            // Use batch endpoint - single call for all quotes with messages and unread counts
            const response = await quoteService.getQuoteUpdates();
            const quotesWithUpdates = response.quotes || [];

            // Detect status changes
            const updatedQuotes = quotesWithUpdates.map(quote => {
                const prevStatus = previousStatuses.current[quote.id];
                const hasStatusChanged = prevStatus && prevStatus !== quote.status;

                return {
                    ...quote,
                    statusChanged: hasStatusChanged,
                    statusChangedAt: hasStatusChanged ? new Date().getTime() : quote.statusChangedAt
                };
            });

            // Update previous statuses (using ref to avoid re-render)
            const newStatuses = {};
            updatedQuotes.forEach(q => {
                newStatuses[q.id] = q.status;
            });
            previousStatuses.current = newStatuses;

            // Sort by most recent activity
            const sorted = updatedQuotes.sort(
                (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
            );

            setQuotes(sorted);
        } catch (error) {
            if (!silent) {
                console.error('Error fetching quotes:', error);
                toast.error(t('error_fetching_quotes') || 'Erro ao buscar orçamentos');
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, [t]);

    const getDefaultTab = React.useCallback((quote) => {
        // If pending or waiting for proposal, show details by default
        if (quote.status === 'PENDING') return 'details';
        // Otherwise, show chat
        return 'chat';
    }, []);

    const handleSelectQuote = React.useCallback((quote) => {
        setSelectedQuote(quote);
        setShowMobileDetail(true);
        setShowBodyMap(true); // Keep body map visible when changing quotes
        setShowProposalForm(false); // Reset form visibility
        setActiveTab(getDefaultTab(quote));

        // Pre-fill form if data exists
        if (quote.priceMin) {
            setProposalPriceMin(quote.priceMin);
        } else if (quote.artwork?.pricing?.minPrice) {
            setProposalPriceMin(quote.artwork.pricing.minPrice);
        } else {
            setProposalPriceMin('');
        }

        if (quote.priceMax) {
            setProposalPriceMax(quote.priceMax);
        } else if (quote.artwork?.pricing?.maxPrice) {
            setProposalPriceMax(quote.artwork.pricing.maxPrice);
        } else {
            setProposalPriceMax('');
        }

        if (quote.suggestedDate) setProposalDate(quote.suggestedDate);

        if (quote.id) {
            // Mark as viewed on server
            quoteService.markAsViewed(quote.id).catch(err => console.error(err));
        }

        // Update unread count for this quote immediately
        setQuotes(prev => prev.map(q =>
            q.id === quote.id ? { ...q, unreadCount: 0 } : q
        ));
    }, [getDefaultTab]);

    // Initial load
    React.useEffect(() => {
        fetchQuotes();
    }, [fetchQuotes]);

    // Handle initial selection from state navigation
    React.useEffect(() => {
        if (!initialNavRef.current && location.state?.selectedQuoteId && quotes.length > 0) {
            const targetQuote = quotes.find(q => q.id === location.state.selectedQuoteId);
            if (targetQuote && (!selectedQuote || selectedQuote.id !== targetQuote.id)) {
                let tab = 'active';
                const status = targetQuote.status;
                if (['CLIENT_APPROVED', 'BOOKED'].includes(status)) {
                    tab = 'approved';
                } else if (['DECLINED', 'CLIENT_REJECTED'].includes(status)) {
                    tab = 'rejected';
                }
                
                setStatusFilter(tab);
                handleSelectQuote(targetQuote);
                initialNavRef.current = true; // Mark as handled to prevent forcing back to this quote
                
                if (window.history.replaceState) {
                    window.history.replaceState({}, document.title);
                }
            }
        }
    }, [quotes, location.state, selectedQuote, handleSelectQuote]);

    // Global polling for all quotes (replaces individual per-chat polling)
    React.useEffect(() => {
        const interval = setInterval(async () => {
            // Fetch quote updates silently
            await fetchQuotes(true);

            // If a quote is selected, also refresh its messages
            if (selectedQuote) {
                fetchMessages(selectedQuote.id, true);
            }
        }, 10000); // Poll every 10 seconds

        return () => clearInterval(interval);
    }, [fetchQuotes, selectedQuote]);

    const fetchMessages = async (quoteId, silent = false) => {
        if (!silent) setLoadingMessages(true);
        try {
            const msgs = await quoteService.getMessages(quoteId);
            setMessages(msgs || []);
        } catch (error) {
            if (!silent) console.error('Error fetching messages:', error);
        } finally {
            if (!silent) setLoadingMessages(false);
        }
    };

    const handleQuickAction = async (newStatus, additionalData = {}) => {
        if (!selectedQuote) return;

        // Validation for proposal
        if (newStatus === 'ARTIST_PROPOSED') {
            if (!additionalData.priceMin || !additionalData.priceMax || !additionalData.suggestedDate) {
                toast.error(t('fill_proposal_fields') || 'Preencha todos os campos da proposta');
                return;
            }
        }

        try {
            const updated = { ...selectedQuote, status: newStatus, ...additionalData };
            setQuotes(quotes.map(q => q.id === selectedQuote.id ? updated : q));
            setSelectedQuote(updated);
            await quoteService.updateQuoteStatus(selectedQuote.id, newStatus, additionalData);

            if (newStatus === 'ARTIST_PROPOSED') {
                setShowProposalForm(false);
                toast.success(t('proposal_sent') || 'Proposta enviada com sucesso');
            } else if (newStatus === 'CLIENT_APPROVED') {
                toast.success(t('proposal_approved') || 'Solicitação aprovada!');
            } else if (newStatus === 'CLIENT_REJECTED') {
                toast.success(t('proposal_rejected') || 'Proposta recusada.');
            } else {
                toast.success(t('status_updated') || 'Status atualizado com sucesso');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error(t('error_updating_status') || 'Erro ao atualizar status');
            fetchQuotes();
        }
    };

    const handleSendProposal = () => {
        handleQuickAction('ARTIST_PROPOSED', {
            priceMin: proposalPriceMin,
            priceMax: proposalPriceMax,
            suggestedDate: proposalDate
        });
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedQuote) return;

        try {
            const msgContent = newMessage;
            setNewMessage('');
            await quoteService.addMessage(selectedQuote.id, msgContent);
            fetchMessages(selectedQuote.id, true);

            // Auto-reply logic: if I am the ARTIST of this quote and it was PENDING
            if (selectedQuote.viewContext === 'received' && selectedQuote.status === 'PENDING') {
                handleQuickAction('REPLIED');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error(t('error_sending_message') || 'Falha ao enviar mensagem');
        }
    };

    const handleFinalizeChat = async (quoteId) => {
        try {
            await quoteService.finalizeChat(quoteId);
            toast.success(t('chat_finalized') || 'Chat finalizado com sucesso!');

            // Refresh quotes to update UI
            await fetchQuotes(true);

            // Update selected quote
            if (selectedQuote?.id === quoteId) {
                const updatedQuote = quotes.find(q => q.id === quoteId);
                if (updatedQuote) {
                    setSelectedQuote({ ...updatedQuote, isChatFinalized: true });
                }
            }
        } catch (error) {
            console.error('Error finalizing chat:', error);
            toast.error(error.response?.data?.message || t('error_finalizing_chat') || 'Erro ao finalizar chat');
        }
    };

    const handleBackToList = () => {
        setSelectedQuote(null);
        setShowMobileDetail(false);
    };

    // Swipe gesture handlers
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        touchEndX.current = e.touches[0].clientX; // Reset end to start on new touch
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        const swipeDistance = touchEndX.current - touchStartX.current;
        // Strict threshold: must be a clear left-to-right swipe > 100px
        if (swipeDistance > 100) {
            handleBackToList();
        }
        // Always reset to avoid stale values
        touchStartX.current = 0;
        touchEndX.current = 0;
    };

    const getStatusText = (status) => {
        const keys = {
            PENDING: 'status_pending',
            REPLIED: 'status_replied',
            BOOKED: 'status_booked',
            DECLINED: 'status_declined',
            ARTIST_PROPOSED: 'status_proposed',
            STATUS_PROPOSED: 'status_proposed', // Fallback if backend returns this
            CLIENT_APPROVED: 'status_approved',
            CLIENT_REJECTED: 'status_rejected',
            ARCHIVED: 'status_archived'
        };
        return t(keys[status]) || status;
    };

    if (loading) return <div className="loading-container">{t('loading')}</div>;

    const filteredQuotes = quotes.filter(quote => {
        const now = new Date();
        const twoDaysAgo = new Date(now.getTime() - (2 * 24 * 60 * 60 * 1000));
        const quoteUpdatedAt = new Date(quote.updatedAt);

        if (statusFilter === 'active') {
            // Ativas: quotes in negotiation
            return ['PENDING', 'ARTIST_PROPOSED', 'REPLIED'].includes(quote.status);
        }
        if (statusFilter === 'approved') {
            // Aprovadas: approved quotes - hide only if 2 days old AND finalized
            const isApproved = ['CLIENT_APPROVED', 'BOOKED'].includes(quote.status);
            const isOld = quoteUpdatedAt < twoDaysAgo;
            const isFinalized = quote.isChatFinalized;

            // Show if: approved AND (not old OR not finalized)
            return isApproved && !(isOld && isFinalized);
        }
        if (statusFilter === 'rejected') {
            // Recusadas: rejected quotes from last 2 days (no finalize needed)
            return ['DECLINED', 'CLIENT_REJECTED'].includes(quote.status) && quoteUpdatedAt >= twoDaysAgo;
        }
        return true;
    });

    const now = new Date();
    const twoDaysAgo = new Date(now.getTime() - (2 * 24 * 60 * 60 * 1000));

    const activeCount = quotes.filter(q => ['PENDING', 'ARTIST_PROPOSED', 'REPLIED'].includes(q.status)).length;
    const activeUnread = quotes.filter(q => ['PENDING', 'ARTIST_PROPOSED', 'REPLIED'].includes(q.status) && q.unreadCount > 0).length;

    const approvedCount = quotes.filter(q => {
        const updatedAt = new Date(q.updatedAt);
        const isApproved = ['CLIENT_APPROVED', 'BOOKED'].includes(q.status);
        const isOld = updatedAt < twoDaysAgo;
        const isFinalized = q.isChatFinalized;
        return isApproved && !(isOld && isFinalized);
    }).length;
    const approvedUnread = quotes.filter(q => {
        const updatedAt = new Date(q.updatedAt);
        const isApproved = ['CLIENT_APPROVED', 'BOOKED'].includes(q.status);
        const isOld = updatedAt < twoDaysAgo;
        const isFinalized = q.isChatFinalized;
        return isApproved && !(isOld && isFinalized) && q.unreadCount > 0;
    }).length;

    const rejectedCount = quotes.filter(q => {
        const updatedAt = new Date(q.updatedAt);
        return ['DECLINED', 'CLIENT_REJECTED'].includes(q.status) && updatedAt >= twoDaysAgo;
    }).length;
    const rejectedUnread = quotes.filter(q => {
        const updatedAt = new Date(q.updatedAt);
        return ['DECLINED', 'CLIENT_REJECTED'].includes(q.status) && updatedAt >= twoDaysAgo && q.unreadCount > 0;
    }).length;

    const handleFilterChange = (filter) => {
        setStatusFilter(filter);
        setSelectedQuote(null);
        setShowMobileDetail(false);
    };

    // Calculate total unread across all tabs
    const totalUnread = activeUnread + approvedUnread + rejectedUnread;

    // Simplified handleDownloadCalendar is no longer needed in this component as it's handled by CalendarButton

    return (
        <div className="quotes-page-container">
            <h1 className="page-title">
                {t('quotes') || 'Solicitações'}
                {totalUnread > 0 && (
                    <span className="global-unread-badge">{totalUnread}</span>
                )}
            </h1>

            <div className={`quotes-layout ${selectedQuote ? 'has-selection' : ''}`}>
                {/* Sidebar List */}
                <div className="quotes-list-sidebar">
                    <div className="filter-tabs">
                        <button className={`filter-tab ${statusFilter === 'active' ? 'active' : ''}`} onClick={() => handleFilterChange('active')}>
                            {t('filter_active') || 'Ativas'}
                            <span className="count-badge">{activeCount}</span>
                            {activeUnread > 0 && <span className="notification-dot"></span>}
                        </button>
                        <button className={`filter-tab ${statusFilter === 'approved' ? 'active' : ''}`} onClick={() => handleFilterChange('approved')}>
                            {t('filter_approved') || 'Aprovadas'}
                            <span className="count-badge">{approvedCount}</span>
                            {approvedUnread > 0 && <span className="notification-dot"></span>}
                        </button>
                        <button className={`filter-tab ${statusFilter === 'rejected' ? 'active' : ''}`} onClick={() => handleFilterChange('rejected')}>
                            {t('filter_rejected') || 'Recusadas'}
                            <span className="count-badge">{rejectedCount}</span>
                            {rejectedUnread > 0 && <span className="notification-dot"></span>}
                        </button>
                    </div>

                    <div className="quotes-list">
                        {filteredQuotes.length === 0 ? (
                            <p className="no-quotes">{t('no_quotes_in_filter')}</p>
                        ) : (
                            filteredQuotes.map(quote => (
                                <div key={quote.id} className={`quote-item ${selectedQuote?.id === quote.id ? 'active' : ''} ${quote.statusChanged ? 'status-changed' : ''}`} onClick={() => handleSelectQuote(quote)}>
                                    <div className="quote-item-header">
                                        <div className="quote-item-main-info">
                                            <span className="client-name">
                                                {quote.viewContext === 'received'
                                                    ? t('from_client', { name: quote.client?.username || t('unknown_client') })
                                                    : t('to_artist', { name: quote.artistProfile?.artistName || t('unknown_artist') })}
                                            </span>
                                            <span className={`view-context-tag ${quote.viewContext}`}>
                                                {quote.viewContext === 'received' ? t('received') : t('sent')}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span className={`status-badge ${quote.status.toLowerCase()}`}>{getStatusText(quote.status)}</span>
                                            {quote.unreadCount > 0 && (
                                                <span className="unread-badge">{quote.unreadCount}</span>
                                            )}
                                        </div>
                                    </div>
                                    <p className="quote-item-preview">
                                        {quote.style ? `${quote.style} - ` : ''}{quote.bodyPart}
                                    </p>
                                    <span className="quote-date">{new Date(quote.createdAt).toLocaleDateString()}</span>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Main Content Area */}
                <div className={`quote-main-content ${showMobileDetail ? 'visible' : ''}`} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
                    {selectedQuote ? (
                        <>
                            <div className="swipe-indicator"><div className="swipe-bar"></div></div>
                            <div className="view-switcher-tabs">
                                <button
                                    className={`tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('chat')}
                                >
                                    {t('tab_chat')}
                                </button>
                                <button
                                    className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('details')}
                                >
                                    {t('tab_details')}
                                </button>
                            </div>

                            {activeTab === 'details' ? (
                                <div className="exclusive-details-view">
                                    <div className="detail-header">
                                        <div className="header-user-info">
                                            {selectedQuote.viewContext === 'received' ? (
                                                <Link to={`/profile/${selectedQuote.client?.username}`} className="header-profile-link" target="_blank" rel="noopener noreferrer">
                                                    {selectedQuote.client?.profiles?.[0]?.profilePicture ? (
                                                        <img src={selectedQuote.client.profiles[0].profilePicture} alt="" className="header-avatar" />
                                                    ) : (
                                                        <div className="header-avatar-placeholder">{selectedQuote.client?.username?.[0]?.toUpperCase()}</div>
                                                    )}
                                                    <div className="header-text">
                                                        <h2>
                                                            {selectedQuote.client?.username}
                                                            {selectedQuote.client?.profiles?.[0]?.isVerified && <VerifiedBadge size={16} />}
                                                        </h2>
                                                        <span className={`status-badge ${selectedQuote.status.toLowerCase()}`}>{getStatusText(selectedQuote.status)}</span>
                                                    </div>
                                                </Link>
                                            ) : (
                                                <Link to={`/profile/${selectedQuote.artistProfile?.user?.username}`} className="header-profile-link" target="_blank" rel="noopener noreferrer">
                                                    {selectedQuote.artistProfile?.profilePicture ? (
                                                        <img src={selectedQuote.artistProfile.profilePicture} alt="" className="header-avatar" />
                                                    ) : (
                                                        <div className="header-avatar-placeholder">{selectedQuote.artistProfile?.artistName?.[0]?.toUpperCase()}</div>
                                                    )}
                                                    <div className="header-text">
                                                        <h2>
                                                            {selectedQuote.artistProfile?.artistName}
                                                            {selectedQuote.artistProfile?.isVerified && <VerifiedBadge size={16} />}
                                                        </h2>
                                                        <span className={`status-badge ${selectedQuote.status.toLowerCase()}`}>{getStatusText(selectedQuote.status)}</span>
                                                    </div>
                                                </Link>
                                            )}
                                        </div>
                                        {selectedQuote.viewContext === 'received' && (
                                            <div className="quick-actions">
                                                {/* Artist Actions */}
                                                {selectedQuote.status === 'PENDING' && !showProposalForm && (
                                                    <>
                                                        <button className="btn-action btn-decline" onClick={() => handleQuickAction('DECLINED')}>{t('decline')}</button>
                                                        <button className="btn-action btn-accept" onClick={() => setShowProposalForm(true)}>{t('make_proposal') || 'Fazer Proposta'}</button>
                                                    </>
                                                )}

                                                {showProposalForm && (
                                                    <div className="proposal-form">
                                                        <h4>{t('send_proposal_title') || 'Enviar Proposta'}</h4>
                                                        <div className="form-row">
                                                            <input type="number" placeholder={t('min_price') || "Min Price"} value={proposalPriceMin} onChange={e => setProposalPriceMin(e.target.value)} />
                                                            <input type="number" placeholder={t('max_price') || "Max Price"} value={proposalPriceMax} onChange={e => setProposalPriceMax(e.target.value)} />
                                                        </div>
                                                        <input type="text" placeholder={t('suggested_date_placeholder') || "Ex: Outubro 2024"} value={proposalDate} onChange={e => setProposalDate(e.target.value)} />
                                                        <div className="form-actions-row">
                                                            <button className="btn-cancel-small" onClick={() => setShowProposalForm(false)}>{t('cancel')}</button>
                                                            <button className="btn-submit-small" onClick={handleSendProposal}>{t('send')}</button>
                                                        </div>
                                                    </div>
                                                )}

                                                {selectedQuote.status === 'ARTIST_PROPOSED' && (
                                                    <div className="status-banner info">
                                                        {t('waiting_client_approval') || 'Aguardando aprovação do cliente.'}
                                                    </div>
                                                )}

                                                {selectedQuote.status === 'CLIENT_REJECTED' && (
                                                    <div className="status-banner warning">
                                                        <p>{t('client_rejected_msg') || 'Cliente recusou a proposta anterior.'}</p>
                                                        <button className="btn-action btn-retry" onClick={() => setShowProposalForm(true)}>{t('send_new_proposal') || 'Enviar Nova Proposta'}</button>
                                                    </div>
                                                )}

                                                {selectedQuote.status === 'CLIENT_APPROVED' && (
                                                    <div className="booking-action-panel">
                                                        <div className="panel-header">
                                                            <h4>{t('proposal_approved_title')}</h4>
                                                        </div>
                                                        <div className="panel-content">
                                                            <div className="panel-grid">
                                                                <div className="panel-item">
                                                                    <label>{t('approved_value')}</label>
                                                                    <span>R${selectedQuote.priceMin} - R${selectedQuote.priceMax}</span>
                                                                </div>
                                                                <div className="panel-item">
                                                                    <label>{t('approved_date')}</label>
                                                                    <span>{selectedQuote.suggestedDate}</span>
                                                                </div>
                                                            </div>
                                                            <div className="panel-actions">
                                                                <div className="date-input-box">
                                                                    <label>{t('confirm_booking_datetime')}</label>
                                                                    <input
                                                                        type="date"
                                                                        value={refinedBookingDate}
                                                                        onChange={(e) => setRefinedBookingDate(e.target.value)}
                                                                        onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                                                    />
                                                                </div>
                                                                <button
                                                                    className="btn-mark-booked"
                                                                    onClick={() => handleQuickAction('BOOKED', { suggestedDate: refinedBookingDate })}
                                                                >
                                                                    {t('mark_booked')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {selectedQuote.status === 'BOOKED' && (
                                                    <CalendarButton
                                                        event={{
                                                            title: `Tattoo: ${selectedQuote.style || selectedQuote.bodyPart}`,
                                                            description: `NovaInk Appointment\nClient: ${selectedQuote.client?.username}\nDetails: ${selectedQuote.description}\nPrice: ${selectedQuote.priceMin} - ${selectedQuote.priceMax}`,
                                                            location: selectedQuote.artistProfile?.studio || "Studio",
                                                            startTime: selectedQuote.suggestedDate && selectedQuote.suggestedDate.includes('T') ? new Date(selectedQuote.suggestedDate) : new Date(),
                                                            url: window.location.href
                                                        }}
                                                        className="btn-action"
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {selectedQuote.viewContext === 'sent' && (
                                            <div className="quick-actions">
                                                {/* Client Actions */}
                                                {selectedQuote.status === 'ARTIST_PROPOSED' && (
                                                    <div className="client-review-box">
                                                        <h4>{t('artist_proposal') || 'Proposta do Tatuador'}</h4>
                                                        <div className="proposal-details">
                                                            <p><strong>{t('price_range') || 'Faixa de Preço'}:</strong> R$ {selectedQuote.priceMin} - R$ {selectedQuote.priceMax}</p>
                                                            <p><strong>{t('suggested_date') || 'Previsão'}:</strong> {selectedQuote.suggestedDate}</p>
                                                        </div>
                                                        <div className="client-actions-row">
                                                            <button className="btn-action btn-decline" onClick={() => handleQuickAction('CLIENT_REJECTED', { rejectionReason: 'Price/Date mismatch' })}>{t('reject') || 'Recusar'}</button>
                                                            <button className="btn-action btn-accept" onClick={() => handleQuickAction('CLIENT_APPROVED')}>{t('approve') || 'Aprovar'}</button>
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedQuote.status === 'CLIENT_APPROVED' && (
                                                    <div className="status-banner success">
                                                        <p>{t('proposal_approved_wait_booking') || 'Você aprovou! Agora combine o agendamento no chat.'}</p>
                                                        {selectedQuote.priceMax && (
                                                            {/* Mercado Pago Button temporariamente oculto para não subir para PRD 
                                                            <button
                                                                className="btn-pay-mercadopago"
                                                                disabled={paymentLoading}
                                                                onClick={async () => {
                                                                    setPaymentLoading(true);
                                                                    try {
                                                                        const result = await paymentService.createPreference(selectedQuote.id);
                                                                        const url = result.sandboxInitPoint || result.initPoint;
                                                                        window.open(url, '_blank');
                                                                    } catch (err) {
                                                                        console.error('Payment error:', err);
                                                                        toast.error('Erro ao iniciar pagamento. Tente novamente.');
                                                                    } finally {
                                                                        setPaymentLoading(false);
                                                                    }
                                                                }}
                                                            >
                                                                {paymentLoading ? '⏳ Aguarde...' : '💳 Pagar via Mercado Pago'}
                                                            </button>
                                                            */}
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {selectedQuote.artwork && (
                                        <div className="artwork-context-card">
                                            <div className="artwork-context-header">
                                                <span className="context-label">{t('quote_for_artwork') || 'Solicitação para Arte:'}</span>
                                                <Link to={`/artworks/${selectedQuote.artworkId}`} className="btn-view-artwork" target="_blank">
                                                    {t('view_artwork_detail') || 'Ver Detalhes da Arte'} ↗
                                                </Link>
                                            </div>
                                            <Link to={`/artworks/${selectedQuote.artworkId}`} className="artwork-mini-info" target="_blank" rel="noopener noreferrer">
                                                <img src={selectedQuote.artwork.imageUrl} alt={selectedQuote.artwork.title} className="artwork-mini-thumb" />
                                                <div className="artwork-mini-details">
                                                    <h4>{selectedQuote.artwork.title}</h4>
                                                    {selectedQuote.artwork.pricing && (
                                                        <p className="artwork-original-price">
                                                            {t('original_price') || 'Preço Original'}:
                                                            <strong> R$ {selectedQuote.artwork.pricing.minPrice} - R$ {selectedQuote.artwork.pricing.maxPrice}</strong>
                                                        </p>
                                                    )}
                                                </div>
                                            </Link>
                                        </div>
                                    )}

                                    <div className="quote-specs">
                                        <div className="spec-item"><label>{t('style')}</label><span>{selectedQuote.style || '-'}</span></div>
                                        <div className="spec-item"><label>{t('body_part')}</label><span>{selectedQuote.bodyPart || '-'}</span></div>
                                        <div className="spec-item"><label>{t('size')}</label><span>{selectedQuote.size || '-'}</span></div>
                                    </div>

                                    <div className="quote-description">
                                        <h4 className="description-label">{t('tattoo_description') || 'Descrição da Tatuagem'}</h4>
                                        <p>{selectedQuote.description}</p>
                                    </div>

                                    {selectedQuote.referenceImages && selectedQuote.referenceImages.length > 0 && (
                                        <div className="quote-reference-images">
                                            <label>{t('reference_photos') || 'Fotos de Referência'}</label>
                                            <div className="reference-grid">
                                                {selectedQuote.referenceImages.map((img, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="reference-thumb"
                                                        onClick={() => window.open(img, '_blank')}
                                                    >
                                                        <img src={img} alt={`Ref ${idx}`} onError={(e) => e.target.style.display = 'none'} />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {selectedQuote.mannequinData && (
                                        <div className="quote-collapsible-section">
                                            <button
                                                className="btn-toggle-map"
                                                onClick={() => setShowBodyMap(!showBodyMap)}
                                            >
                                                {showBodyMap ? `🔼 ${t('hide_body_map') || 'Ocultar Mapa Corporal'}` : `👤 ${t('view_body_map') || 'Ver Mapa Corporal'}`}
                                            </button>

                                            <div className={`collapsible-content ${showBodyMap ? 'expanded' : ''}`}>
                                                <div className="quote-drawing-preview">
                                                    <div className="drawing-container">
                                                        <DrawableMannequin
                                                            readOnly={true}
                                                            initialData={selectedQuote.mannequinData}
                                                            heightCm={selectedQuote.client?.height || 170}
                                                            onAreaChange={() => { }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="chat-section">
                                    <div className="chat-security-disclaimer">
                                        <p>{t('chat_security_warning')}</p>
                                    </div>
                                    <div className="messages-list">
                                        {loadingMessages ? (
                                            <div style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>{t('loading')}</div>
                                        ) : messages.length === 0 ? (
                                            <div style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>{t('no_messages_yet')}</div>
                                        ) : (
                                            messages.map(msg => {
                                                const isMe = msg.senderId === currentUserId;
                                                return (
                                                    <div key={msg.id} className={`message ${isMe ? 'sent' : 'received'}`}>
                                                        <div className="message-content">{msg.content}</div>
                                                        <span className="message-time">{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                    </div>
                                                );
                                            })
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>

                                    {['PENDING', 'REPLIED'].includes(selectedQuote.status) ? (
                                        <div className="chat-locked-overlay-strip">
                                            {selectedQuote.viewContext === 'sent' ? (
                                                <p>🔒 {t('chat_locked_until_reply') || 'Chat bloqueado até o envio da proposta.'}</p>
                                            ) : (
                                                <div className="locked-strip-content">
                                                    <p>🔒 {t('chat_locked_until_proposal_short') || 'Envie uma proposta para liberar o chat.'}</p>
                                                    <button
                                                        className="btn-link-action"
                                                        onClick={() => setActiveTab('details')}
                                                    >
                                                        {t('go_to_details') || 'Ir para Informações'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ) : selectedQuote.isChatFinalized ? (
                                        <div className="chat-locked-message">
                                            <p>🔒 {t('chat_finalized_message') || 'Chat finalizado. Não é mais possível enviar mensagens.'}</p>
                                        </div>
                                    ) : (
                                        <div className="chat-input-wrapper">
                                            <form className="chat-input-area" onSubmit={handleSendMessage}>
                                                <textarea
                                                    placeholder={t('type_message_placeholder')}
                                                    value={newMessage}
                                                    onChange={(e) => setNewMessage(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                            e.preventDefault();
                                                            handleSendMessage(e);
                                                        }
                                                    }}
                                                />
                                                <button type="submit" className="btn-send" disabled={!newMessage.trim()}>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                                                    <span>{t('send')}</span>
                                                </button>
                                            </form>
                                            {selectedQuote.viewContext === 'received' && ['CLIENT_APPROVED', 'BOOKED'].includes(selectedQuote.status) && (
                                                <button
                                                    className="btn-finalize-chat"
                                                    onClick={() => handleFinalizeChat(selectedQuote.id)}
                                                >
                                                    ✓ {t('finalize_chat') || 'Finalizar Chat'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="empty-selection">
                            <p>{t('select_quote_msg') || 'Selecione um orçamento para ver os detalhes.'}</p>
                        </div>
                    )}
                </div>
            </div >
        </div >
    );
};

export default QuotesPage;
